import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getPolicy, saveMyPolicy, calcLate } from '../lib/policy';

const field = 'mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white';

const Section = ({ n, title, desc, children }) => (
  <section className="rounded-2xl border border-gray-100 p-4 space-y-3">
    <div className="flex items-start gap-3">
      <span className="w-6 h-6 rounded-full bg-sky-700 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{n}</span>
      <div><h4 className="font-bold text-gray-900 text-sm">{title}</h4><p className="text-xs text-gray-400 mt-0.5">{desc}</p></div>
    </div>
    <div className="space-y-3 sm:pl-9">{children}</div>
  </section>
);
const Num = ({ label, hint, unit, value, onChange, ...rest }) => (
  <label className="block text-xs font-semibold text-gray-600">{label}
    <div className="flex items-center gap-2"><input type="number" value={value} onChange={(e) => onChange(e.target.value)} className={field} {...rest} />{unit && <span className="text-xs text-gray-400 mt-1 whitespace-nowrap">{unit}</span>}</div>
    {hint && <span className="block text-[11px] font-normal text-gray-400 mt-1">{hint}</span>}
  </label>
);

// Reglas de asistencia y horas propias de cada organización. Las aplica el servidor al marcar.
export default function RulesSettings({ onSaved, showToast }) {
  const start = getPolicy();
  const [tol, setTol] = useState(start.lateness.tolerance_minutes);
  const [mode, setMode] = useState(start.lateness.mode);
  const [round, setRound] = useState(start.lateness.rounding_minutes);
  const [margin, setMargin] = useState(start.hours.diff_margin_minutes);
  const [recover, setRecover] = useState(start.hours.recover_late_time);
  const [daily, setDaily] = useState(start.hours.daily_hours);
  const [days, setDays] = useState(start.hours.month_days);
  const [absF, setAbsF] = useState(start.payroll.absence_factor);
  const [minsDef, setMinsDef] = useState(start.payroll.minutes_debt_deduct_default);
  const [freeLate, setFreeLate] = useState(start.payroll.late_free_count);
  const [lateAmt, setLateAmt] = useState(start.payroll.late_fixed_amount);
  const [otWd, setOtWd] = useState(start.overtime.weekday_multiplier);
  const [otWe, setOtWe] = useState(start.overtime.weekend_multiplier);
  const [otSat, setOtSat] = useState(start.overtime.saturday_multiplier ?? start.overtime.weekend_multiplier);
  const [otSun, setOtSun] = useState(start.overtime.sunday_multiplier ?? start.overtime.weekend_multiplier);
  const [otHol, setOtHol] = useState(start.overtime.holiday_multiplier ?? 2);
  const [nightOn, setNightOn] = useState(!!start.overtime.night_enabled);
  const [nightFrom, setNightFrom] = useState(start.overtime.night_start || '21:00');
  const [nightTo, setNightTo] = useState(start.overtime.night_end || '06:00');
  const [nightMult, setNightMult] = useState(start.overtime.night_multiplier ?? 1.5);
  const [rules, setRules] = useState((start.overtime.rules || []).map((r) => ({ ...r, days: r.days || [] })));
  const [holCount, setHolCount] = useState(null);
  useEffect(() => { const y = new Date().getFullYear(); supabase.from('holidays').select('id', { count: 'exact', head: true }).gte('date', `${y}-01-01`).lte('date', `${y}-12-31`).then(({ count }) => setHolCount(count ?? 0)); }, []);
  const addRule = () => setRules((r) => [...r, { name: '', days: [], from: '00:00', to: '06:00', multiplier: 1.5 }]);
  const setRule = (i, patch) => setRules((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const toggleDay = (i, d) => setRules((r) => r.map((x, j) => (j === i ? { ...x, days: x.days.includes(d) ? x.days.filter((z) => z !== d) : [...x.days, d] } : x)));
  const [otAuto, setOtAuto] = useState(start.overtime.auto !== false);
  const [otMin, setOtMin] = useState(start.overtime.min_minutes ?? 30);
  const [alertOn, setAlertOn] = useState(start.overtime.alert_enabled !== false);
  const [alertMin, setAlertMin] = useState(start.overtime.alert_minutes ?? 120);
  const [justPct, setJustPct] = useState(start.payroll.justified_deduct_pct ?? 0);
  const [needCert, setNeedCert] = useState(!!start.payroll.justification_requires_certificate);
  const [saving, setSaving] = useState(false);

  const TEMPLATES = {
    'Oficina / servicios': { t: 10, m: 'all', r: 5, g: 10, rec: true, d: 8, days: 22, wd: 1.5, we: 2 },
    'Comercio': { t: 5, m: 'subtract', r: 1, g: 5, rec: false, d: 8, days: 26, wd: 1.5, we: 2 },
    'Gastronomía': { t: 10, m: 'all', r: 15, g: 15, rec: false, d: 8, days: 26, wd: 1.5, we: 2 },
    'Educación': { t: 5, m: 'all', r: 1, g: 5, rec: false, d: 6, days: 20, wd: 1, we: 1 },
    'ONG / comunidad': { t: 15, m: 'all', r: 1, g: 5, rec: true, d: 8, days: 20, wd: 1, we: 1 },
  };
  const applyTemplate = (k) => {
    const x = TEMPLATES[k];
    setTol(x.t); setMode(x.m); setRound(x.r); setMargin(x.g); setRecover(x.rec); setDaily(x.d); setDays(x.days); setOtWd(x.wd); setOtWe(x.we); setOtSat(x.we); setOtSun(x.we);
    showToast?.(`Valores de "${k}" cargados. Revisalos y tocá Guardar.`);
  };

  const draft = { lateness: { tolerance_minutes: Number(tol) || 0, mode, rounding_minutes: Number(round) }, hours: {} };
  const example = (arrive) => calcLate(arrive, null, draft);

  const save = async () => {
    setSaving(true);
    try {
      await saveMyPolicy({
        lateness: { tolerance_minutes: Number(tol), mode, rounding_minutes: Number(round) },
        hours: { diff_margin_minutes: Number(margin), recover_late_time: recover, daily_hours: Number(daily), month_days: Number(days) },
        payroll: { absence_factor: Number(absF), minutes_debt_deduct_default: minsDef, late_free_count: Number(freeLate), late_fixed_amount: Number(lateAmt), justified_deduct_pct: Number(justPct), justification_requires_certificate: needCert },
        overtime: { weekday_multiplier: Number(otWd), weekend_multiplier: Number(otWe), saturday_multiplier: Number(otSat), sunday_multiplier: Number(otSun), holiday_multiplier: Number(otHol), night_enabled: nightOn, night_start: nightFrom, night_end: nightTo, night_multiplier: Number(nightMult), rules: rules.map((r) => ({ name: r.name, days: r.days, from: r.from, to: r.to, multiplier: Number(r.multiplier) })), auto: otAuto, min_minutes: Number(otMin), alert_enabled: alertOn, alert_minutes: Number(alertMin) },
      });
      showToast?.('Reglas actualizadas');
      await onSaved?.();
    } catch (e) { showToast?.(e.message || 'No se pudieron guardar las reglas', 'error'); }
    setSaving(false);
  };

  const factorOpts = [[0, 'No se descuentan'], [1, 'Se descuenta el día (lo normal)'], [1.5, 'Se descuenta 1,5 días'], [2, 'Se descuenta doble']];
  if (!factorOpts.some(([v]) => v === Number(absF))) factorOpts.push([Number(absF), `Factor ${absF}`]);
  const pctOpts = [0, 25, 50, 75, 100];
  if (!pctOpts.includes(Number(justPct))) pctOpts.push(Number(justPct));

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 space-y-4">
      <div>
        <h3 className="font-bold text-gray-900 text-sm">Reglas de asistencia y horas</h3>
        <p className="text-xs text-gray-400 mt-0.5">Acá definís cómo se cuentan las tardanzas, la jornada y los descuentos del sueldo. Los cambios valen para las marcaciones nuevas y los sueldos que liquides desde ahora.</p>
      </div>

      <div className="rounded-2xl bg-sky-50 border border-sky-100 p-4">
        <p className="text-xs font-bold text-sky-800">¿Primera vez? Empezá con un modelo</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {Object.keys(TEMPLATES).map((k) => (
            <button key={k} type="button" onClick={() => applyTemplate(k)} className="px-3 py-1.5 rounded-full text-xs font-bold border border-sky-200 text-sky-700 bg-white">{k}</button>
          ))}
        </div>
        <p className="text-[11px] text-sky-700/70 mt-2">Son valores orientativos: ajustalos a tu convenio o criterio. No se aplican hasta que toques "Guardar reglas".</p>
      </div>

      <Section n="1" title="Llegadas tarde" desc="Cuánto se tolera y cómo se cuentan los minutos de tardanza.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Num label="Tolerancia" unit="minutos" hint="Hasta acá no se considera tarde." min="0" max="120" value={tol} onChange={setTol} />
          <label className="block text-xs font-semibold text-gray-600">Redondear la tardanza a
            <select value={round} onChange={(e) => setRound(e.target.value)} className={field}>
              {[1, 5, 10, 15, 30].map((n) => <option key={n} value={n}>{n === 1 ? 'El minuto exacto' : `Bloques de ${n} min`}</option>)}
            </select>
          </label>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1.5">Si se pasa de la tolerancia, la tardanza cuenta…</p>
          {[
            ['all', 'Todos los minutos', `Con tolerancia ${tol || 0}: si llega ${(Number(tol) || 0) + 5} min tarde, la tardanza es de ${(Number(tol) || 0) + 5} min.`],
            ['subtract', 'Solo lo que excede la tolerancia', `Con tolerancia ${tol || 0}: si llega ${(Number(tol) || 0) + 5} min tarde, la tardanza es de 5 min.`],
          ].map(([v, l, d]) => (
            <label key={v} className={`flex gap-3 items-start p-3 rounded-2xl border-2 mb-2 cursor-pointer ${mode === v ? 'border-sky-200 bg-sky-50' : 'border-gray-100'}`}>
              <input type="radio" name="late-mode" checked={mode === v} onChange={() => setMode(v)} className="mt-1" />
              <span><b className="text-sm text-gray-800">{l}</b><span className="block text-xs text-gray-500">{d}</span></span>
            </label>
          ))}
          <p className="text-xs text-sky-700 bg-sky-50 rounded-xl px-3 py-2">
            Resultado: llega 10 min tarde → <b>{example(10)} min</b> · 20 min tarde → <b>{example(20)} min</b> · 40 min tarde → <b>{example(40)} min</b> de tardanza.
          </p>
        </div>
        <label className="flex items-start gap-3 text-xs text-gray-600">
          <input type="checkbox" checked={recover} onChange={(e) => setRecover(e.target.checked)} className="mt-0.5" />
          <span><b className="text-gray-800">Recuperar el tiempo de tardanza.</b> Quien llega tarde tiene que salir más tarde para completar la jornada.</span>
        </label>
      </Section>

      <Section n="2" title="Jornada de trabajo" desc="Sirve para calcular horas extra, horas faltantes y el valor de la hora.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Num label="Jornada diaria" unit="horas" hint="Ej.: 8" min="1" max="24" step="0.5" value={daily} onChange={setDaily} />
          <Num label="Días laborables por mes" unit="días" hint="Para el valor de la hora. Ej.: 20 a 26" min="1" max="31" value={days} onChange={setDays} />
          <Num label="Margen de diferencia" unit="minutos" hint="Diferencias menores no cuentan como extra ni faltante." min="0" max="60" value={margin} onChange={setMargin} />
        </div>
      </Section>

      <Section n="3" title="Faltas y sueldo" desc="Qué se descuenta cuando alguien falta.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-xs font-semibold text-gray-600">Falta sin justificar
            <select value={absF} onChange={(e) => setAbsF(e.target.value)} className={field}>
              {factorOpts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <span className="block text-[11px] font-normal text-gray-400 mt-1">Cuánto del sueldo de ese día se descuenta.</span>
          </label>
          <label className="block text-xs font-semibold text-gray-600">Falta justificada (con certificado u otro motivo)
            <select value={justPct} onChange={(e) => setJustPct(e.target.value)} className={field}>
              {pctOpts.map((v) => <option key={v} value={v}>{v === 0 ? 'No se descuenta' : v === 100 ? 'Se descuenta completa' : `Se descuenta el ${v}%`}</option>)}
            </select>
            <span className="block text-[11px] font-normal text-gray-400 mt-1">Vacaciones y licencias aprobadas nunca se descuentan. Podés definir otro porcentaje para un empleado en su ficha.</span>
          </label>
        </div>
        <label className="flex items-start gap-3 text-xs text-gray-600">
          <input type="checkbox" checked={needCert} onChange={(e) => setNeedCert(e.target.checked)} className="mt-0.5" />
          <span><b className="text-gray-800">Pedir certificado para justificar una falta.</b> Si está activo, el empleado no puede enviar el pedido sin adjuntar un archivo.</span>
        </label>
      </Section>

      <Section n="4" title="Tardanzas en el sueldo" desc="Si querés descontar plata por llegar tarde (opcional).">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Num label="Tardanzas sin descuento por mes" unit="veces" hint="Las primeras llegadas tarde no se cobran." min="0" max="31" value={freeLate} onChange={setFreeLate} />
          <Num label="Descuento por cada tardanza" unit="$" hint="0 = no se descuenta nada." min="0" value={lateAmt} onChange={setLateAmt} />
        </div>
        <label className="flex items-start gap-3 text-xs text-gray-600">
          <input type="checkbox" checked={minsDef} onChange={(e) => setMinsDef(e.target.checked)} className="mt-0.5" />
          <span>Descontar por defecto los minutos que el empleado le debe a la jornada al liquidar.</span>
        </label>
      </Section>

      <Section n="5" title="Horas extra" desc="Cuánto se paga de más según el día y la hora. Se calcula minuto a minuto, así que una misma jornada puede tener tramos con distinto recargo.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Num label="Lunes a viernes" unit="×" min="1" max="5" step="0.25" value={otWd} onChange={setOtWd} />
          <Num label="Sábado" unit="×" min="1" max="5" step="0.25" value={otSat} onChange={setOtSat} />
          <Num label="Domingo" unit="×" min="1" max="5" step="0.25" value={otSun} onChange={setOtSun} />
          <Num label="Feriados" unit="×" min="1" max="5" step="0.25" value={otHol} onChange={setOtHol} />
        </div>
        <p className="text-[11px] text-gray-400">1 = valor normal · 1,5 = 50 % más · 2 = doble.</p>
        <p className={`text-xs rounded-xl px-3 py-2 ${holCount === 0 ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-sky-700'}`}>
          {holCount === 0
            ? 'Todavía no cargaste los feriados de este año. Cargalos en la sección Feriados para que el recargo se aplique solo.'
            : `Los feriados se toman automáticamente de tu calendario de Feriados${holCount ? ` (${holCount} este año)` : ''}: si alguien trabaja un feriado, se aplica ese recargo sin que tengas que elegirlo.`}
        </p>
        <div className="rounded-2xl border border-gray-100 p-3 space-y-2">
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={nightOn} onChange={(e) => setNightOn(e.target.checked)} className="mt-0.5" />
            <span><b>Recargo nocturno.</b> Las horas extra que caen en esta franja se pagan con un recargo mayor.</span>
          </label>
          {nightOn && (
            <div className="grid grid-cols-3 gap-3">
              <label className="block text-xs font-semibold text-gray-600">Desde<input type="time" value={nightFrom} onChange={(e) => setNightFrom(e.target.value)} className={field} /></label>
              <label className="block text-xs font-semibold text-gray-600">Hasta<input type="time" value={nightTo} onChange={(e) => setNightTo(e.target.value)} className={field} /></label>
              <Num label="Recargo" unit="×" min="1" max="5" step="0.25" value={nightMult} onChange={setNightMult} />
            </div>
          )}
          {nightOn && <p className="text-[11px] text-gray-400">Si un tramo es feriado y nocturno a la vez, se aplica el recargo más alto (no se suman).</p>}
        </div>
        <div className="rounded-2xl border border-gray-100 p-3 space-y-2">
          <div className="flex items-center justify-between"><p className="text-sm font-bold text-gray-700">Reglas propias</p><button type="button" onClick={addRule} disabled={rules.length >= 12} className="text-xs font-bold text-sky-700 disabled:opacity-40">+ Agregar regla</button></div>
          {rules.length === 0 && <p className="text-xs text-gray-400">Opcional. Por ejemplo: «Sábado desde las 13:00 → ×2» o «Domingos de 6 a 14 → ×1,75».</p>}
          {rules.map((r, i) => (
            <div key={i} className="rounded-xl bg-gray-50 p-3 space-y-2">
              <div className="flex gap-2">
                <input value={r.name} onChange={(e) => setRule(i, { name: e.target.value })} placeholder="Nombre (ej.: Sábado tarde)" maxLength={40} className={field} />
                <button type="button" onClick={() => setRules((x) => x.filter((_, j) => j !== i))} className="text-xs font-bold text-red-500 px-2">Quitar</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((l, d) => (
                  <button key={d} type="button" onClick={() => toggleDay(i, d)} className={`w-8 h-8 rounded-full text-xs font-bold border ${r.days.includes(d) ? 'bg-sky-700 text-white border-sky-700' : 'bg-white text-gray-500 border-gray-200'}`}>{l}</button>
                ))}
                <span className="text-[11px] text-gray-400 self-center ml-1">{r.days.length === 0 ? 'Todos los días' : ''}</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="block text-xs font-semibold text-gray-600">Desde<input type="time" value={r.from} onChange={(e) => setRule(i, { from: e.target.value })} className={field} /></label>
                <label className="block text-xs font-semibold text-gray-600">Hasta<input type="time" value={r.to} onChange={(e) => setRule(i, { to: e.target.value })} className={field} /></label>
                <Num label="Recargo" unit="×" min="1" max="5" step="0.25" value={r.multiplier} onChange={(v) => setRule(i, { multiplier: v })} />
              </div>
            </div>
          ))}
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-600 mt-3">
          <input type="checkbox" checked={otAuto} onChange={(e) => setOtAuto(e.target.checked)} className="mt-0.5" />
          <span>Detectar horas extra automáticamente (entrada antes o salida después del horario, o trabajo en día no laboral). Quedan «por aprobar» y no suman al sueldo hasta que las confirmes.</span>
        </label>
        {otAuto && <div className="mt-2"><Num label="Mínimo para generar la hora extra" unit="minutos" min="1" max="240" step="5" value={otMin} onChange={setOtMin} /></div>}
        <label className="flex items-start gap-2 text-sm text-gray-600 mt-3">
          <input type="checkbox" checked={alertOn} onChange={(e) => setAlertOn(e.target.checked)} className="mt-0.5" />
          <span>Avisar al empleado y al encargado cuando alguien <b>ya registró</b> muy fuera de su horario o en un día que no trabaja.<span className="block text-[11px] text-gray-400 mt-0.5">Es un aviso posterior: el registro se permite y se marca. Si en Configuración → Formas de registrar está activo «Horario habilitado para registrar la entrada», las entradas fuera de ese margen se bloquean antes y no llegan a generar este aviso.</span></span>
        </label>
        {alertOn && <div className="mt-2"><Num label="Se considera «muy fuera de horario» a partir de" unit="minutos" min="15" max="600" step="15" value={alertMin} onChange={setAlertMin} /></div>}
      </Section>

      <button onClick={save} disabled={saving} className="w-full py-3 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">
        {saving ? 'Guardando…' : 'Guardar reglas'}
      </button>
    </div>
  );
}
